const SURFACES = Object.freeze({ flood: 1, prism: 2, basalt: 3, moonlit: 4, garden: 5 });

/** Select a presentation-only material family. Unknown keys resolve to neutral
 * stone and return false; callers keep original-world materials outside these
 * explicit imported worlds. No terrain height or collision policy changes. */
export function setSkyThemedSurface(material, surface) {
  const known = Object.hasOwn(SURFACES, surface);
  material.uniforms.worldKind.value = known ? SURFACES[surface] : 0;
  return known;
}

/** Source-textured surface for imported worlds03–07. Existing terrain vertices
 * and their normals supply all shape and lighting. time is travelled metres,
 * matching174's terrain UV motion, not a second animation clock. Caller owns
 * both maps; disposing this material does not dispose or mutate their samplers.
 * Water and molten lava are surface appearance, never new collision/hazards.
 * flowTime is active simulation seconds; reduced motion holds it at zero. */
export function createSkyThemedGroundMaterial(T, landMap, basaltMap) {
  return new T.ShaderMaterial({
    name: 'sky-imported-world-surface',
    fog: true,
    uniforms: {
      ...T.UniformsUtils.clone(T.UniformsLib.fog),
      time: { value: 0 },
      flowTime: { value: 0 },
      worldKind: { value: 0 },
      landMap: { value: landMap },
      basaltMap: { value: basaltMap ?? landMap },
      lavaMap: { value: basaltMap ?? landMap },
    },
    vertexShader: `
      varying vec3 localPoint;
      varying vec3 worldPoint;
      varying vec3 worldNormal;
      #include <fog_pars_vertex>
      void main() {
        localPoint = position;
        worldPoint = (modelMatrix * vec4(position, 1.0)).xyz;
        worldNormal = normalize(mat3(modelMatrix) * normal);
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: `
      uniform float time;
      uniform float flowTime;
      uniform int worldKind;
      uniform sampler2D landMap;
      uniform sampler2D basaltMap;
      uniform sampler2D lavaMap;
      varying vec3 localPoint;
      varying vec3 worldPoint;
      varying vec3 worldNormal;
      #include <common>
      #include <fog_pars_fragment>
      float luminanceOf(vec3 value) {
        return dot(value, vec3(0.2126, 0.7152, 0.0722));
      }
      void main() {
        // Shared world coordinates join the road to its surrounding caldera.
        vec2 uv = vec2(worldPoint.x, -worldPoint.z + time) * 0.085;
        vec3 source = texture2D(landMap, uv).rgb;
        float grain = luminanceOf(source);
        // Authored texture features remain the dominant local variation. Wet
        // areas follow its dark seams and existing low terrain, not new noise.
        vec3 albedo = vec3(grain) * vec3(0.42, 0.45, 0.47);
        float wetness = 0.0;
        float roughness = 0.72;
        vec3 emission = vec3(0.0);
        float surfaceRelief = 0.0;
        if (worldKind == 1) {
          float low = 1.0 - smoothstep(0.2, 1.3, localPoint.z);
          float channel = (1.0 - smoothstep(0.13, 0.38, grain)) * low;
          albedo = vec3(grain) * vec3(0.24, 0.33, 0.42);
          albedo = mix(albedo, vec3(0.018, 0.045, 0.065), channel * 0.78);
          wetness = 0.35 + channel * 0.5;
          roughness = 0.28;
        } else if (worldKind == 2) {
          albedo = vec3(grain) * vec3(0.24, 0.28, 0.38);
          // Mineral highlights follow pale inclusions in the source image.
          albedo += vec3(0.045, 0.055, 0.075) * smoothstep(0.53, 0.83, grain);
          wetness = 0.12;
          roughness = 0.4;
        } else if (worldKind == 3) {
          vec3 basalt = texture2D(basaltMap, uv * 1.8).rgb;
          // Actual painted crust supplies both the small relief and hot seams.
          // Slow translation carries rafts through the river; raised banks stay
          // fixed. Emission retains every source channel rather than drawing
          // smooth noise contours over the artwork.
          vec2 flow = uv * 0.85 + vec2(0.0, -flowTime * 0.004);
          vec3 lava = texture2D(lavaMap, flow).rgb;
          float river = 1.0 - smoothstep(18.0, 38.0, abs(worldPoint.x));
          float heat = smoothstep(0.07, 0.5, lava.r - max(lava.g, lava.b));
          float shimmer = 0.94 + 0.06 * sin(flowTime * 0.7 + flow.y * 9.0);
          albedo = mix(basalt * 1.1, lava * mix(1.15, 0.45, heat), river);
          emission = lava * heat * river * 1.15 * shimmer;
          surfaceRelief = mix(luminanceOf(basalt), luminanceOf(lava) * (1.0 - heat), river) * 0.18;
          wetness = heat * river * 0.24;
          roughness = 0.86 - heat * river * 0.32;
        } else if (worldKind == 4) {
          albedo = vec3(grain) * vec3(0.13, 0.21, 0.29);
          wetness = 0.62;
          roughness = 0.3;
        } else if (worldKind == 5) {
          float moss = smoothstep(0.015, 0.14, source.g - source.r * 0.8);
          vec3 stone = vec3(0.12, 0.17, 0.10) + vec3(grain) * vec3(0.25, 0.32, 0.18);
          albedo = mix(stone, source * vec3(0.45, 0.7, 0.32), moss * 0.65);
          wetness = 0.16;
          roughness = 0.78;
        }
        vec3 normal = normalize(worldNormal);
        vec3 tangentX = dFdx(worldPoint), tangentY = dFdy(worldPoint);
        vec3 basisX = cross(tangentY, normal), basisY = cross(normal, tangentX);
        float determinant = dot(tangentX, basisX);
        if (worldKind == 3 && abs(determinant) > 0.000001) {
          vec3 gradient = dFdx(surfaceRelief) * basisX + dFdy(surfaceRelief) * basisY;
          normal = normalize(abs(determinant) * normal - sign(determinant) * gradient);
        }
        vec3 light = normalize(vec3(-0.4, 0.85, 0.35));
        vec3 eye = normalize(cameraPosition - worldPoint);
        vec3 halfVector = normalize(light + eye);
        float diffuse = max(0.0, dot(normal, light));
        float specular = pow(max(0.0, dot(normal, halfVector)), mix(72.0, 12.0, roughness));
        float fresnel = 0.04 + 0.22 * pow(1.0 - max(0.0, dot(normal, eye)), 5.0);
        vec3 color = albedo * (0.4 + diffuse * 0.7) + emission;
        color += vec3(0.55, 0.66, 0.76) * specular * fresnel * wetness * 0.65;
        gl_FragColor = vec4(color, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
        #include <fog_fragment>
      }`,
  });
}
